import { useMemo } from 'react'
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Tooltip } from 'chart.js'
import { Bar } from 'react-chartjs-2'
import type { DayOfWeekStat } from '@/utils/analytics'

ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip)

interface DayOfWeekChartProps {
  stats: DayOfWeekStat[]
}

export default function DayOfWeekChart({ stats }: DayOfWeekChartProps) {
  const data = useMemo(() => {
    const maxAvg = Math.max(...stats.map(s => s.avgDrinks), 1)
    return {
      labels: stats.map(s => s.label),
      datasets: [
        {
          label: 'Avg drinks',
          data: stats.map(s => Math.round(s.avgDrinks * 100) / 100),
          backgroundColor: stats.map(s => {
            const ratio = s.avgDrinks / maxAvg
            // Teal at low, orange at high
            if (ratio > 0.75) return '#ef4444'
            if (ratio > 0.5) return '#f97316'
            if (ratio > 0.25) return '#f59e0b'
            return '#2dd4bf'
          }),
          borderRadius: 4,
        },
      ],
    }
  }, [stats])

  const options = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        backgroundColor: 'rgb(30, 41, 59)',
        titleColor: 'rgb(248, 250, 252)',
        bodyColor: 'rgb(148, 163, 184)',
        borderColor: 'rgb(71, 85, 105)',
        borderWidth: 1,
        callbacks: {
          label: (item: { dataIndex: number; parsed: { y: number } }) => {
            const s = stats[item.dataIndex]
            const dryDays = s.occurrences - s.drinkingOccurrences
            const dryPct = s.occurrences > 0 ? Math.round((dryDays / s.occurrences) * 100) : 0
            return [
              `${item.parsed.y.toFixed(2)} avg drinks per ${s.label}`,
              `Drank on ${s.drinkingOccurrences} of ${s.occurrences} (${100 - dryPct}%)`,
              `Dry on ${dryDays} (${dryPct}%)`,
              `${s.totalDrinks} drinks · ${s.avgStdDrinks.toFixed(1)} avg std`,
            ]
          },
        },
      },
    },
    scales: {
      x: {
        ticks: { color: 'rgb(148, 163, 184)' },
        grid: { display: false },
      },
      y: {
        beginAtZero: true,
        ticks: { color: 'rgb(148, 163, 184)' },
        grid: { color: 'rgba(71, 85, 105, 0.3)' },
      },
    },
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  }), [stats]) as any

  const hasData = stats.some(s => s.totalDrinks > 0)
  if (!hasData) {
    return (
      <div className="card">
        <h2 className="text-sm font-semibold text-[var(--text-primary)] mb-2">Drinks by day of week</h2>
        <p className="text-sm text-[var(--text-secondary)]">No data in this period.</p>
      </div>
    )
  }

  return (
    <div className="card">
      <div className="flex items-baseline justify-between mb-3">
        <h2 className="text-sm font-semibold text-[var(--text-primary)]">Drinks by day of week</h2>
        <p className="text-xs text-[var(--text-secondary)]">average per occurrence</p>
      </div>
      <div className="h-48">
        <Bar data={data} options={options} />
      </div>
    </div>
  )
}
